import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { IDowntimeDetailsHeader } from "./DowntimeDetailsHeadersInterfaces";
import EastIcon from "@mui/icons-material/East";
import { useStyles } from "./DowntimeDetailsHeaders.styles";
import { useTranslation } from "react-i18next";
import { calculateShiftDate, getCurrentShift } from "utilities/utilities";
import { IShift } from "../../../LiveDowntimeReportAPIInterfaces";

export const DowntimeDetailsHeader = ({ downtimeSelected }: IDowntimeDetailsHeader) => {
    const classes = useStyles();
    const [nowDate, setNowDate] = useState(moment().tz(downtimeSelected.millTimezone));
    const { t } = useTranslation();
    const currentTime = moment().tz(downtimeSelected.millTimezone).format("HH:mm:ss");
    const [currentShift, setCurrentShift] = useState<IShift | undefined>(undefined);

    useEffect(() => {
        const oneSecondinterval = setInterval(() => {
            setNowDate(moment().tz(downtimeSelected.millTimezone));
        }, 1000);
        return () => clearInterval(oneSecondinterval);
    }, [downtimeSelected.millTimezone]);

    // current shift calculation
    useEffect(() => {
        //sort shifts from earliest to latest by start_time
        const sortedShifts = downtimeSelected.productionLine.production_line_shifts.slice().map((shift) => ({
            ...shift,
            shift: {
                ...shift.shift,
                shift_historic: shift.shift.shift_historic
                    .slice()
                    .sort((a, b) => new Date(b.effective_at).getTime() - new Date(a.effective_at).getTime())
                    .filter((shift) => moment.utc(shift.effective_at).isSameOrBefore(moment())),
            },
        }));

        //find the current shift if any
        const currentShift = getCurrentShift(sortedShifts, downtimeSelected.millTimezone, currentTime);

        setCurrentShift(currentShift?.shift);
    }, [downtimeSelected, currentTime]);

    const currentDownTime = useMemo(() => {
        if (downtimeSelected && downtimeSelected.status === "down" && downtimeSelected.current) {
            const nowDateParsed = moment(nowDate, "DD/MM/YYYY HH:mm:ss");
            if (currentShift) {
                const shiftHourValues = currentShift.shift_historic[0]?.start_time.split(":");

                const shiftWithDate = calculateShiftDate(
                    Number(shiftHourValues[0]),
                    Number(shiftHourValues[1]),
                    Number(shiftHourValues[2]),
                    true,
                    downtimeSelected.millTimezone,
                );

                const maxStartTime = moment.max(moment.utc(downtimeSelected.current), shiftWithDate);

                const currentTime = moment.duration(nowDateParsed.diff(maxStartTime));

                return currentTime.format("hh:mm:ss", { trim: false });
            }
        }
        return "00:00:00";
    }, [downtimeSelected, nowDate, currentShift]);

    const showHeaderInformation = useMemo(() => {
        return Boolean(currentDownTime !== "00:00:00" && downtimeSelected.failureTypeName);
    }, [currentDownTime, downtimeSelected.failureTypeName]);

    return (
        <>
            {showHeaderInformation && (
                <div className={classes.cardResume}>
                    {(!downtimeSelected.failure_code ||
                        (downtimeSelected.failure_code && downtimeSelected.status !== "end_of_shift")) && (
                        <div className={classes.cardResumeItem}>
                            <div className={classes.itemLabel}>
                                {t("locations.mill_dashboards.overall_downtime_summary.current_downtime")}
                            </div>
                            <div
                                className={
                                    currentDownTime === "00:00:00"
                                        ? classes.cardResumeItemValueNumber
                                        : classes.cardResumeItemValueNumberCurrent
                                }
                            >
                                {currentDownTime}
                            </div>
                        </div>
                    )}

                    <div className={classes.cardResumeItem}>
                        <div className={classes.itemLabel}>
                            {t("locations.mill_dashboards.overall_downtime_summary.current_event")}
                        </div>
                        <div className={classes.cardResumeItemValue}>
                            {downtimeSelected.failureTypeName}{" "}
                            {downtimeSelected.failureTypeName &&
                                (downtimeSelected.failureCodeName ||
                                    downtimeSelected.areaName ||
                                    downtimeSelected.functionalLocationName) && <EastIcon />}
                            {downtimeSelected.areaName && (
                                <>
                                    {downtimeSelected.areaName} <EastIcon />
                                </>
                            )}
                            {downtimeSelected.functionalLocationName && (
                                <>
                                    {downtimeSelected.functionalLocationName}
                                    <EastIcon />
                                </>
                            )}
                            {downtimeSelected.failureCodeName && downtimeSelected.failureCodeName}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
